import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoginCard from "./components/LoginCard";
import LoginCodeStep from "./components/LoginCodeStep";
import LoginEmailStep from "./components/LoginEmailStep";
import { useResendCooldown } from "./hooks/useResendCooldown";
import { validateLoginCode, validateLoginEmail } from "./loginValidation";
import { requestOtp, verifyOtp } from "../../../store/slices/authSlice";
import "./login.scss";

function Login() {
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [step, setStep] = useState("email");
	const [errorObj, setErrorObj] = useState({});

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { loading, isAuthenticated, redirectTo } = useSelector((s) => s.auth);
	const { countdown, start: startCountdown, reset: resetCountdown } =
		useResendCooldown();

	const isRequestingOtp = loading === "requestOtp";
	const isVerifyingOtp = loading === "verifyOtp";

	useEffect(() => {
		if (isAuthenticated) {
			navigate(redirectTo || "/projects", { replace: true });
		}
	}, [isAuthenticated, redirectTo, navigate]);

	const onRequestCode = async () => {
		const emailError = validateLoginEmail(email);
		if (emailError) {
			setErrorObj({ email: emailError });
			return;
		}

		const result = await dispatch(requestOtp(email));
		if (result.error) {
			setErrorObj({ email: result.payload });
			return;
		}

		setCode("");
		setErrorObj({});
		setStep("code");
		startCountdown();
	};

	const onVerifyCode = async () => {
		const codeError = validateLoginCode(code);
		if (codeError) {
			setErrorObj({ code: codeError });
			return;
		}

		const result = await dispatch(
			verifyOtp({ email, code: code.toUpperCase() }),
		);
		if (result.error) {
			setErrorObj({ code: result.payload });
			setCode("");
		}
	};

	const onChangeEmail = () => {
		setStep("email");
		setCode("");
		setErrorObj({});
		resetCountdown();
	};

	const handleEmailChange = (value) => {
		setEmail(value);
		setErrorObj({});
	};

	return (
		<LoginCard>
			{step === "email" ? (
				<LoginEmailStep
					email={email}
					emailError={errorObj.email}
					isRequesting={isRequestingOtp}
					onEmailChange={handleEmailChange}
					onSubmit={onRequestCode}
				/>
			) : (
				<LoginCodeStep
					email={email}
					code={code}
					codeError={errorObj.code}
					countdown={countdown}
					isRequesting={isRequestingOtp}
					isVerifying={isVerifyingOtp}
					onCodeChange={setCode}
					onVerify={onVerifyCode}
					onResend={onRequestCode}
					onChangeEmail={onChangeEmail}
				/>
			)}
		</LoginCard>
	);
}

export default Login;
